import { useState } from 'react'
import {
  Alert, Card, Table, Button, Space, Typography, Select, Tag, Row, Col,
  Drawer, Form, message, InputNumber, Input, Divider, Modal,
  Grid, Empty, Spin,
} from 'antd'
import {
  UserSwitchOutlined, PlusOutlined, EditOutlined,
  StopOutlined, CheckCircleOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import NumberWithUnit from '@/shared/components/NumberWithUnit'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

const NUM_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }
const API = ''

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'success',
  BLOCKED: 'warning',
  BLACKLISTED: 'error',
}

const CHANNEL_COLORS: Record<string, string> = {
  DOMESTIC: 'blue',
  EXPORT: 'green',
  DEEMED_EXPORT: 'cyan',
}

const GRADE_COLORS: Record<string, string> = {
  A: 'green',
  B: 'gold',
  C: 'orange',
}

export default function CustomerPage() {
  const screens = useBreakpoint()
  const isMobile = !screens.lg
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [form] = Form.useForm()

  const { data: customers, isLoading } = useQuery({
    queryKey: ['customers', plantId],
    queryFn: () =>
      apiClient.get(`${API}/customers`, { params: { plant_id: plantId, limit: 500 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/customers`, { ...data, plant_id: plantId }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      message.success('Customer created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.patch(`${API}/customers/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      message.success('Customer updated')
      setDrawerOpen(false)
      setEditingCustomer(null)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  // Sprint 0065 — Deactivate / Reactivate customer flow
  const [deactivateCustomer, setDeactivateCustomer] = useState<any>(null)
  const [deactReason, setDeactReason] = useState('')
  const [deactivateMode, setDeactivateMode] = useState<'OFF' | 'ON'>('OFF')

  const deactivateMutation = useMutation({
    mutationFn: async (input: { customer: any; reason: string; mode: 'OFF' | 'ON' }) => {
      const action = input.mode === 'OFF' ? 'deactivate' : 'reactivate'
      return await apiClient.post(`/customers/${input.customer.id}/${action}`, {
        reason: input.reason,
      }).then(r => r.data)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      setDeactivateCustomer(null)
      setDeactReason('')
      message.success(
        vars.mode === 'OFF'
          ? `Customer ${vars.customer.customer_name} deactivated`
          : `Customer ${vars.customer.customer_name} reactivated`,
      )
    },
    onError: (e: any) =>
      message.error(e.response?.data?.detail || 'Action failed'),
  })

  // Sprint 0063 — code-override toggle. Default is auto (greyed-out
  // field showing the next code from the Numbering Series Master).
  // User clicks "Edit code" → field becomes writable for legacy /
  // Tally-compat overrides.
  const [codeOverride, setCodeOverride] = useState(false)
  const [nextCodePreview, setNextCodePreview] = useState<string>('')

  const fetchNextCode = async () => {
    if (!plantId) return
    try {
      const { data } = await apiClient.get('/numbering-series/next-code', {
        params: { document_type: 'CUSTOMER', plant_id: plantId },
      })
      setNextCodePreview(data?.next_code ?? '')
      // Pre-fill the form so the user sees the code without typing.
      form.setFieldValue('customer_code', data?.next_code ?? '')
    } catch {
      setNextCodePreview('')
    }
  }

  const openCreate = () => {
    setEditingCustomer(null)
    setCodeOverride(false)
    form.resetFields()
    setDrawerOpen(true)
    void fetchNextCode()
  }

  const openEdit = (customer: any) => {
    setEditingCustomer(customer)
    form.setFieldsValue(customer)
    setDrawerOpen(true)
  }

  const handleSave = () => {
    form.validateFields().then(vals => {
      if (editingCustomer) {
        updateMut.mutate({ id: editingCustomer.id, data: vals })
      } else {
        createMut.mutate(vals)
      }
    })
  }

  const customerList = Array.isArray(customers) ? customers : []

  const columns = [
    {
      title: 'Code', dataIndex: 'customer_code', key: 'customer_code', width: 110,
      render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
    },
    { title: 'Name', dataIndex: 'customer_name', key: 'customer_name', width: 180 },
    { title: 'City', dataIndex: 'city', key: 'city', width: 100 },
    { title: 'State', dataIndex: 'state', key: 'state', width: 80 },
    {
      title: 'Channel', dataIndex: 'channel', key: 'channel', width: 110,
      render: (v: string) => <Tag color={CHANNEL_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Credit Limit (₹)', dataIndex: 'credit_limit', key: 'credit_limit', width: 130, align: 'right' as const,
      render: (v: number) => v != null ? `₹${Number(v).toLocaleString('en-IN')}` : '—',
    },
    {
      title: 'Outstanding (₹)', dataIndex: 'outstanding_amount', key: 'outstanding_amount', width: 140, align: 'right' as const,
      render: (v: number, r: any) => {
        const pct = r.credit_limit ? (Number(v) / Number(r.credit_limit)) * 100 : 0
        const color = pct > 80 ? 'red' : 'inherit'
        return <span style={{ color }}>₹{Number(v ?? 0).toLocaleString('en-IN')}</span>
      },
    },
    {
      title: 'Grade', dataIndex: 'grade', key: 'grade', width: 70,
      render: (v: string) => <Tag color={GRADE_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    { title: 'Payment Terms', dataIndex: 'payment_terms', key: 'payment_terms', width: 120 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 220,
      render: (_: unknown, r: any) => (
        <Space size={4} wrap>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
          {/* Sprint 0065 — Deactivate / Reactivate */}
          {r.is_active && (
            <Button size="small" danger icon={<StopOutlined />}
              onClick={() => {
                setDeactivateCustomer(r)
                setDeactReason('')
                setDeactivateMode('OFF')
              }}>
              Deactivate
            </Button>
          )}
          {!r.is_active && (
            <Button size="small" type="primary" ghost
              icon={<CheckCircleOutlined />}
              onClick={() => {
                setDeactivateCustomer(r)
                setDeactReason('')
                setDeactivateMode('ON')
              }}>
              Reactivate
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <UserSwitchOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Customers
      </Title>
      <Typography.Text type="secondary">Customer master with credit management</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col flex="auto" />
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Customer
            </Button>
          </Col>
        </Row>

        {isMobile ? (
          isLoading ? <Spin /> :
          customerList.length === 0 ? <Empty description="No customers" /> :
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {customerList.map((r: any) => {
              const pct = r.credit_limit ? (Number(r.outstanding_amount ?? 0) / Number(r.credit_limit)) * 100 : 0
              const overLimit = pct > 80
              return (
                <Card key={r.id} size="small" hoverable
                  styles={{ body: { padding: 14 } }}>
                  <Row align="middle" justify="space-between" wrap={false} gutter={8}>
                    <Col flex="auto" style={{ minWidth: 0 }}>
                      <Space size={6} wrap>
                        <Text strong style={{ fontFamily: 'monospace', fontSize: 13, ...NUM_STYLE }}>
                          {r.customer_code}
                        </Text>
                        <Tag color={STATUS_COLORS[r.status] ?? 'default'}
                             style={{ fontSize: 10, margin: 0 }}>{r.status}</Tag>
                      </Space>
                    </Col>
                    <Col flex="none">
                      <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(r)}
                        style={{ width: 44, height: 44 }} />
                    </Col>
                  </Row>

                  <div style={{ marginTop: 4, fontSize: 15, fontWeight: 500,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.customer_name}
                  </div>

                  <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                    {[r.city, r.state].filter(Boolean).join(', ') || '—'}
                  </div>

                  <Row gutter={6} style={{ marginTop: 8 }} wrap>
                    <Col><Tag color={CHANNEL_COLORS[r.channel] ?? 'default'}
                              style={{ margin: 0, fontSize: 10 }}>{r.channel}</Tag></Col>
                    {r.grade && <Col><Tag color={GRADE_COLORS[r.grade] ?? 'default'}
                                            style={{ margin: 0, fontSize: 10 }}>Grade {r.grade}</Tag></Col>}
                    {r.payment_terms && <Col><Tag style={{ margin: 0, fontSize: 10 }}>{r.payment_terms}</Tag></Col>}
                  </Row>

                  {(r.credit_limit != null || r.outstanding_amount != null) && (
                    <Row gutter={8} style={{ marginTop: 10 }}>
                      <Col span={12}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Credit Limit</div>
                        <div style={{ ...NUM_STYLE, fontWeight: 600 }}>
                          {r.credit_limit != null ? `₹${Number(r.credit_limit).toLocaleString('en-IN')}` : '—'}
                        </div>
                      </Col>
                      <Col span={12}>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Outstanding</div>
                        <div style={{ ...NUM_STYLE, fontWeight: 600,
                                      color: overLimit ? '#cf1322' : '#262626' }}>
                          ₹{Number(r.outstanding_amount ?? 0).toLocaleString('en-IN')}
                        </div>
                      </Col>
                    </Row>
                  )}
                </Card>
              )
            })}
          </Space>
        ) : (
        <Table
          dataSource={customerList}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 1200 }}
        />
        )}
      </Card>

      <Drawer
        title={editingCustomer ? `Edit: ${editingCustomer.customer_name}` : 'New Customer'}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingCustomer(null); form.resetFields() }}
        width={540}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); setEditingCustomer(null); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending || updateMut.isPending}
                onClick={handleSave}
              >
                Save
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={10}>
              {/* Sprint 0063 — Auto-generated by default. Code field is
                  read-only with a refresh-preview button + an "Edit code"
                  toggle for legacy / Tally compat overrides. */}
              <Form.Item name="customer_code"
                label={
                  <Space size={4}>
                    <span>Customer Code</span>
                    {!editingCustomer && !codeOverride && (
                      <span style={{ fontSize: 10, color: '#1677ff' }}>
                        (auto)
                      </span>
                    )}
                    {!editingCustomer && (
                      <a style={{ fontSize: 11 }}
                         onClick={() => {
                           setCodeOverride(v => !v)
                           if (!codeOverride) {
                             // Switching to override — clear so user types fresh
                             form.setFieldValue('customer_code', '')
                           } else {
                             // Switching back to auto — refresh preview
                             void fetchNextCode()
                           }
                         }}>
                        {codeOverride ? '↺ Use auto' : '✎ Edit code'}
                      </a>
                    )}
                  </Space>
                }
                rules={[{ required: codeOverride, message: 'Code required when overriding' }]}>
                <Input placeholder={nextCodePreview || 'auto-generated'}
                  disabled={!editingCustomer && !codeOverride}
                  readOnly={!editingCustomer && !codeOverride} />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="customer_name" label="Customer Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="gstin" label="GSTIN">
                <Input placeholder="22AAAAA0000A1Z5" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pan" label="PAN">
                <Input placeholder="AAAAA0000A" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Contact</Divider>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="contact_person" label="Contact Person">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mobile" label="Mobile">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="email" label="Email">
            <Input />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="city" label="City">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="state" label="State">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Financial</Divider>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="payment_terms" label="Payment Terms">
                <Select options={[
                  { value: 'CASH', label: 'Cash' },
                  { value: '7_DAYS', label: '7 Days' },
                  { value: '15_DAYS', label: '15 Days' },
                  { value: '30_DAYS', label: '30 Days' },
                  { value: '45_DAYS', label: '45 Days' },
                  { value: '60_DAYS', label: '60 Days' },
                  { value: '90_DAYS', label: '90 Days' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="credit_limit" label="Credit Limit (₹)">
                <InputNumber style={{ width: '100%' }} min={0} prefix="₹" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="tds_rate" label="TDS Rate (%)">
                <NumberWithUnit unit="%" style={{ width: '100%' }} min={0} max={100} precision={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="grade" label="Customer Grade">
                <Select options={[
                  { value: 'A', label: 'A' },
                  { value: 'B', label: 'B' },
                  { value: 'C', label: 'C' },
                ]} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="channel" label="Channel">
                <Select options={[
                  { value: 'DOMESTIC', label: 'Domestic' },
                  { value: 'EXPORT', label: 'Export' },
                  { value: 'DEEMED_EXPORT', label: 'Deemed Export' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select options={[
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'BLOCKED', label: 'Blocked' },
                  { value: 'BLACKLISTED', label: 'Blacklisted' },
                ]} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="block_reason" label="Block Reason">
            <Input.TextArea rows={2} placeholder="Reason if blocked or blacklisted" />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Sprint 0065 — Deactivate / Reactivate customer modal */}
      <Modal
        title={
          <Space>
            {deactivateMode === 'OFF' && <StopOutlined style={{ color: '#ff4d4f' }} />}
            {deactivateMode === 'ON' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
            {deactivateMode === 'OFF'
              ? `Deactivate — ${deactivateCustomer?.customer_name ?? ''}`
              : `Reactivate — ${deactivateCustomer?.customer_name ?? ''}`}
          </Space>
        }
        open={!!deactivateCustomer}
        onCancel={() => { setDeactivateCustomer(null); setDeactReason('') }}
        confirmLoading={deactivateMutation.isPending}
        okText={deactivateMode === 'OFF' ? 'Deactivate' : 'Reactivate'}
        okButtonProps={{
          danger: deactivateMode === 'OFF',
          disabled: deactReason.trim().length < 5,
        }}
        onOk={() => {
          if (!deactivateCustomer) return
          deactivateMutation.mutate({
            customer: deactivateCustomer,
            reason: deactReason.trim(),
            mode: deactivateMode,
          })
        }}
      >
        <Alert
          type={deactivateMode === 'OFF' ? 'warning' : 'info'}
          showIcon style={{ marginBottom: 12 }}
          message={deactivateMode === 'OFF'
            ? 'This customer will be hidden from new-transaction pickers.'
            : 'This customer will be restored to the active list.'}
          description={
            <span style={{ fontSize: 12 }}>
              {deactivateMode === 'OFF'
                ? 'Historical sales orders / dispatches / invoices stay linked. The customer can be reactivated later. Reason is recorded.'
                : 'The previous deactivation audit (who/when/why) is cleared from the row.'}
            </span>
          }
        />

        <Form layout="vertical">
          <Form.Item label="Reason" required
            help={`Why ${deactivateMode === 'OFF' ? 'deactivating' : 'reactivating'}? (5–500 chars)`}>
            <Input.TextArea
              rows={3}
              value={deactReason}
              onChange={e => setDeactReason(e.target.value)}
              placeholder={deactivateMode === 'OFF'
                ? 'e.g. customer closed business; long-term default; switched to competitor'
                : 'e.g. customer cleared dues; resuming with revised credit terms'}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>

        {deactivateMode === 'ON' && deactivateCustomer?.deactivation_reason && (
          <Alert
            type="info" showIcon style={{ marginTop: 8 }}
            message="Previous deactivation reason"
            description={
              <span style={{ fontSize: 12 }}>
                {deactivateCustomer.deactivation_reason}
                {deactivateCustomer.deactivated_at && (
                  <span style={{ color: '#888' }}>
                    {' '}— {new Date(deactivateCustomer.deactivated_at).toLocaleDateString()}
                  </span>
                )}
              </span>
            }
          />
        )}
      </Modal>
    </div>
  )
}
